import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/places/Reveal";
import ClientsCarousel from "@/components/places/ClientsCarousel";
import ServicesMarquee from "@/components/places/ServicesMarquee";
import CountUp from "@/components/places/CountUp";
import AnimatedBlob from "@/components/places/AnimatedBlob";
import TestimonialsMarquee from "@/components/places/TestimonialsMarquee";
import SEO from "@/components/places/SEO";
import RotatingHero from "@/components/places/RotatingHero";
import ClassicBadge from "@/components/places/ClassicBadge";
import { ImplementationWrapper } from "@/components/places/InImplementationBadge";
import { useLang } from "@/lib/lang";
import { useContent } from "@/lib/content";

export default function Home({ skipSEO = false }) {
  const { t } = useLang();
  const { stats: STATS, timeline: TIMELINE, cases: CASES, clients: CLIENTS, insights: INSIGHTS, we_are_thumbnails: weAreThumbs = [], home_sections } = useContent();
  const featured = CASES.find((c) => c.featured) || CASES[0];
  const rest = CASES.filter((c) => c.slug !== featured.slug).slice(0, 4);
  // Insights mais recentes primeiro (mesma regra de ordenação da página /insights).
  const topInsights = React.useMemo(() => {
    const keyOf = (i) => `${String(i.year || "0000").padStart(4, "0")}_${i.created_at || i.updated_at || ""}`;
    return [...(INSIGHTS || [])].sort((a, b) => keyOf(b).localeCompare(keyOf(a))).slice(0, 3);
  }, [INSIGHTS]);

  return (
    <>
      {!skipSEO && (
        <SEO
          breadcrumbs={[{ name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" }]}
        />
      )}
      {/* HERO */}
      <section className="theme-light relative overflow-hidden grain pt-36 pb-24 md:pt-44 md:pb-32">
        <AnimatedBlob className="bg-accent-yellow top-20 -left-32 w-[400px] h-[400px]" />
        <AnimatedBlob className="bg-accent-orange bottom-0 right-0 w-[500px] h-[500px] opacity-30" delay={3} />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-widest text-neutral-500 mb-6">
              <span className="w-8 h-px bg-black/30" />
              <span className="text-black font-semibold">BRANDING E MARKETING IMOBILIÁRIO</span>
              <span className="px-2 py-0.5 bg-black text-white text-[10px]">Google Partner</span>
              <span className="text-neutral-500">The Real Estate Legacy</span>
            </div>
            <RotatingHero
              items={home_sections?.hero_rotation || []}
              intervalSeconds={Number(home_sections?.hero_rotation_seconds) || 6}
              fallback={{
                title_html: "Seu empreendimento com <span class=\"text-accent-orange\">narrativa potente</span> e solução <em class=\"not-italic\">completa</em> para ganhar o mercado.",
                subtitle: t.hero.subheadline,
              }}
            />
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/sobre" data-testid="hero-cta-approach" className="btn-sharp bg-black text-white hover:bg-accent-yellow hover:text-black rounded-full">{t.cta.approach} <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/cases" data-testid="hero-cta-legacy" className="btn-sharp border border-black/20 hover:bg-black hover:text-white rounded-full">{t.cta.legacy}</Link>
            </div>
            <div className="mt-14 flex flex-wrap gap-3">
              {["Data Driven", "Design Driven", "IA Driven"].map((d) => (
                <span key={d} className="text-xs font-semibold tracking-wide px-3 py-1.5 border border-black/15 rounded-full">{d}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <ServicesMarquee />

      {/* MANIFESTO */}
      <section className="theme-light py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-5"><div className="text-xs uppercase tracking-widest text-neutral-500">{t.sections.weAre}</div><h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">{t.sections.weAreHeadline || "Aliando dados, mercado, estratégia e criatividade."}</h2></Reveal>
          <Reveal delay={0.1} className="md:col-span-6 md:col-start-7 space-y-6 text-lg text-neutral-700 leading-relaxed">
            <p>Damos forma a produtos, significado a lugares, alma a marcas, expressão a campanhas e magnetismo a anúncios. Com isso, transformamos potenciais compradores em admiradores — e corretores em defensores das lançadoras.</p>
            <p>Aumentamos a percepção de valor sobre os empreendimentos e favorecemos sua conexão com a audiência certa.</p>
            <Link to="/sobre" className="link-underline inline-flex items-center gap-1 text-sm font-semibold mt-4" data-testid="manifesto-link-about">{t.cta.approach} <ArrowUpRight className="w-4 h-4" /></Link>
          </Reveal>
        </div>
        {/* Linha de 4 thumbs 16:9 que ocupa toda a largura no desktop */}
        {weAreThumbs.length > 0 && (
          <div className="mt-16 w-full" data-testid="we-are-thumbnails">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
              {weAreThumbs.slice(0, 4).map((tn, i) => {
                const inner = (
                  <div className="relative w-full aspect-video overflow-hidden bg-neutral-200 group">
                    <img src={tn.src} alt={tn.alt || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    {tn.alt && <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4 opacity-0 group-hover:opacity-100 transition"><span className="text-white text-sm font-display">{tn.alt}</span></div>}
                  </div>
                );
                return tn.link ? (
                  <a key={tn.src || `thumb-${i}`} href={tn.link} target="_blank" rel="noopener noreferrer" data-testid={`we-are-thumb-${i}`}>{inner}</a>
                ) : (
                  <div key={tn.src || `thumb-${i}`} data-testid={`we-are-thumb-${i}`}>{inner}</div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* LEGACY / NUMBERS */}
      <section className="theme-dark relative overflow-hidden grain py-24 md:py-36">
        <AnimatedBlob className="bg-accent-green top-10 right-0 w-[400px] h-[400px] opacity-20" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
          <Reveal>
            <div className="font-display text-4xl md:text-6xl lg:text-7xl leading-tight max-w-4xl">{t.sections.legacy}</div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="font-display text-5xl md:text-6xl lg:text-7xl text-accent-yellow"><CountUp value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} /></div>
                <div className="text-xs uppercase tracking-widest mt-2 text-neutral-400">{s.label}</div>
              </Reveal>
            ))}
          </div>
          <div className="mt-20 grid md:grid-cols-5 gap-6 border-t border-white/10 pt-10">
            {TIMELINE.map((tl) => (
              <div key={tl.year}><div className="text-accent-yellow font-display text-2xl">{tl.year}</div><div className="text-xs text-neutral-500 mt-1">{tl.event}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* CASES */}
      <section className="theme-light py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12"><h2 className="font-display text-4xl md:text-6xl">{t.sections.cases}</h2><Link to="/cases" className="link-underline text-sm font-semibold" data-testid="home-all-cases">{t.cta.allCases} →</Link></div>
          <Reveal>
            <ImplementationWrapper active={!!featured.is_in_implementation} badgeSize="lg">
              <Link to={`/cases/${featured.slug}`} className="group block relative aspect-[16/9] overflow-hidden mb-6" data-testid={`featured-case-${featured.slug}`}>
                <ClassicBadge show={!!featured.is_classic} size="lg" />
                <img src={featured.cover} alt={featured.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 md:p-10 text-white max-w-3xl">
                  <div className="text-xs uppercase tracking-widest mb-2 text-accent-yellow">{(featured.categories || []).join(" · ")}</div>
                  <div className="font-display text-3xl md:text-5xl mb-2">{featured.title}</div>
                  <div className="text-sm opacity-80">{featured.developer} · {featured.city}/{featured.state} · {featured.year}</div>
                </div>
              </Link>
            </ImplementationWrapper>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {rest.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.07}>
                <ImplementationWrapper active={!!c.is_in_implementation} badgeSize="sm">
                  <Link to={`/cases/${c.slug}`} className="group block" data-testid={`home-case-${c.slug}`}>
                    <div className="aspect-[4/3] overflow-hidden bg-neutral-200 relative">
                      <ClassicBadge show={!!c.is_classic} size="sm" />
                      <img src={c.cover} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    </div>
                    <div className="pt-4"><div className="text-xs uppercase tracking-widest text-neutral-500">{c.developer}</div><div className="font-display text-2xl mt-1">{c.title}</div><div className="text-xs text-neutral-500 mt-1">{(c.categories || []).join(" · ")} · {c.year}</div></div>
                  </Link>
                </ImplementationWrapper>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/cases"
              data-testid="home-cases-see-all"
              className="group inline-flex items-center gap-2 text-sm uppercase tracking-widest font-semibold border border-black/15 hover:border-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors"
            >
              {t.cta?.seeAll || "Ver tudo"}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CLIENTS — paginated carousel with CMS-controlled auto-rotate */}
      <ClientsCarousel CLIENTS={CLIENTS} home_sections={home_sections} tSections={t.sections} tCta={t.cta} />

      {/* INSIGHTS */}
      <section className="theme-light py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12"><h2 className="font-display text-4xl md:text-6xl">{t.sections.insights}</h2><Link to="/insights" className="link-underline text-sm font-semibold" data-testid="home-all-insights">{t.cta.allInsights} →</Link></div>
          <div className="grid md:grid-cols-3 gap-6">
            {topInsights.map((i, idx) => (
              <Reveal key={i.slug} delay={idx * 0.07}>
                <Link to={`/insights/${i.slug}`} className="group block" data-testid={`home-insight-${i.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-200"><img src={i.cover} alt={i.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="pt-4"><span className="text-xs px-2 py-1 bg-black text-white">{i.topic}</span><div className="font-display text-xl mt-3">{i.title}</div><div className="text-xs text-neutral-500 mt-2">{i.author} · {i.readMin} min</div></div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsMarquee />

      {/* CTA FINAL */}
      <section className="theme-dark py-24 md:py-32 relative overflow-hidden grain">
        <AnimatedBlob className="bg-accent-yellow top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-20" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center relative">
          <Reveal>
            <div
              className="font-display leading-none"
              style={{ fontSize: `clamp(2.5rem, ${Number(home_sections?.cta_final_font_size) || 6}vw, 8rem)` }}
              data-testid="home-cta-final-title"
            >
              {home_sections?.cta_final_title || t.sections.ctaFinal}
            </div>
            {home_sections?.cta_final_subtitle && <div className="mt-4 text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto">{home_sections.cta_final_subtitle}</div>}
          </Reveal>
          <Reveal delay={0.15}><div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link to="/contato?intent=client" className="bg-accent-yellow text-black btn-sharp rounded-full hover:bg-white transition-colors" data-testid="home-final-cta-client">{t.cta.client} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contato?intent=partner" className="border border-white/30 btn-sharp rounded-full hover:bg-white hover:text-black" data-testid="home-final-cta-partner">{t.cta.partner}</Link>
          </div></Reveal>
        </div>
      </section>
    </>
  );
}
